/**
 * Frota · Manutenção · Plano preventivo.
 *
 * Um item por placa e componente, com o status calculado pelo servidor. O ponto
 * que a tela precisa deixar claro: **a última troca só vale quando alguém a
 * confirma**. O ERP parou de registrar trocas em 2025 e o que restou tem data e
 * km incoerentes; por isso a referência dele aparece AO LADO, com um botão
 * "Adotar", e nunca vira base sozinha (docs/manutencao/11).
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaintenancePlanListDto, MaintenancePlanRowDto, MaintenancePlanStatusDto,
} from "@bi/types";
import { api, ApiError } from "../lib/api";
import { fmtNumber } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { PlateAutocomplete } from "../components/PlateAutocomplete";
import { GroupSelect } from "../features/maintenance/GroupSelect";
import { EmptyState } from "../components/EmptyState";
import { IconWrench } from "../components/icons";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import {
  EXECUTION_SOURCE_LABEL, PLAN_SOURCE_LABEL, PLAN_STATUS, PLAN_STATUS_ORDER, PlanStatusBadge, fmtDay, intervalText,
} from "../features/maintenance/preventive-ui";
import { parseBRNumber } from "../features/maintenance/work-order-ui";

export function MaintenancePreventivePage() {
  const [params, setParams] = useSearchParams();
  const { can } = useCapabilities();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const gerencia = can("workorder.manage");
  const [erro, setErro] = useState<string | null>(null);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());

  const status = (params.get("status") as MaintenancePlanStatusDto | null) ?? "";
  const plate = params.get("plate") ?? "";
  const kind = params.get("kind") ?? "";
  const category = params.get("category") ?? "";
  const group = params.get("group") ?? "";

  const lista = useQuery({
    queryKey: ["maintenance", "preventive", status, plate, kind, category, group],
    queryFn: () => {
      const p = new URLSearchParams();
      if (status) p.set("status", status);
      if (plate) p.set("plate", plate);
      if (kind) p.set("kind", kind);
      if (category) p.set("category", category);
      if (group) p.set("group", group);
      return api.get<MaintenancePlanListDto>(`/maintenance/preventive?${p}`);
    },
  });

  const setParam = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
    setSelecionados(new Set());
  };

  const atualizar = () => { setErro(null); qc.invalidateQueries({ queryKey: ["maintenance"] }); };
  const falha = (msg: string) => (e: unknown) => setErro(e instanceof ApiError ? e.message : msg);

  const rows = lista.data?.rows ?? [];
  const escolhidos = rows.filter((r) => selecionados.has(r.id));
  const placasEscolhidas = new Set(escolhidos.map((r) => r.plate));

  const abrirOS = useMutation({
    mutationFn: (itens: MaintenancePlanRowDto[]) =>
      api.post<{ id: string }>("/maintenance/preventive/work-orders", {
        plate: itens[0]!.plate,
        componentCodes: itens.map((i) => i.componentCode),
      }),
    onSuccess: (o) => { atualizar(); navigate(`/frota/manutencao/ordens/${o.id}`); },
    onError: falha("Não foi possível abrir a O.S."),
  });

  const c = lista.data?.counts;
  const total = c ? PLAN_STATUS_ORDER.reduce((a, s) => a + c[s], 0) : 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Plano Preventivo"
        subtitle="Intervalos por placa: grupo de veículos, ERP e plano padrão da empresa · só frota própria ativa"
        actions={<Link className="btn-secondary" to="/frota/manutencao/ordens?type=preventive">O.S. preventivas</Link>}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {PLAN_STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setParam("status", status === s ? "" : s)}
            className={`card p-3 text-left transition-colors ${status === s ? "ring-2 ring-tm-500/40 border-tm-500" : "hover:bg-surface-muted"}`}
          >
            <p className="text-xs text-gray-500">{PLAN_STATUS[s].label}</p>
            <p className={`text-2xl font-semibold ${s === "vencido" && c?.[s] ? "text-danger-600" : "text-gray-900"}`}>
              {c ? fmtNumber(c[s]) : "—"}
            </p>
          </button>
        ))}
      </div>
      {lista.data && (
        <p className="text-xs text-gray-500">
          {fmtNumber(total)} itens em {fmtNumber(lista.data.plates)} placas
          {lista.data.platesOverdue ? ` · ${lista.data.platesOverdue} placa(s) com item vencido` : ""}.
          {" "}“Sem base” = nenhuma troca confirmada no SIAG — adote a referência do ERP quando ela fizer sentido, ou registre a última troca.
        </p>
      )}

      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <PlateAutocomplete value={plate} onSelect={(p) => setParam("plate", p)} />
        <select className="input w-40" value={kind} onChange={(e) => setParam("kind", e.target.value)}>
          <option value="">Cavalos e carretas</option>
          <option value="tractor">Só cavalos</option>
          <option value="trailer">Só carretas</option>
        </select>
        <GroupSelect value={group} onChange={(g) => setParam("group", g)} withNone />
        <Link className="btn-ghost text-sm" to="/frota/manutencao/grupos">Grupos de veículos</Link>
        <select className="input w-52" value={category} onChange={(e) => setParam("category", e.target.value)}>
          <option value="">Toda categoria</option>
          {(lista.data?.categories ?? []).map((cat) => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        {gerencia && escolhidos.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            {placasEscolhidas.size > 1 && <span className="text-xs text-warning-700">Uma O.S. é de uma placa só.</span>}
            <button className="btn-primary" disabled={placasEscolhidas.size !== 1 || abrirOS.isPending} onClick={() => abrirOS.mutate(escolhidos)}>
              Abrir O.S. preventiva ({escolhidos.length} {escolhidos.length === 1 ? "item" : "itens"})
            </button>
          </div>
        )}
      </div>

      {erro && <div className="card p-3 border-danger-600/30 bg-danger-50 text-sm text-danger-600">{erro}</div>}

      <div className="card overflow-x-auto">
        {lista.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Carregando…</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<IconWrench className="w-5 h-5" />}
            title={total === 0 ? "Plano preventivo ainda não sincronizado" : "Nenhum item com este filtro"}
            description={total === 0 ? "O plano vem do ERP pelo sync noturno (entidade maintenance_plan)." : undefined}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
              <tr>
                {gerencia && <th className="w-8" />}
                <th className="text-left px-3 py-2">Placa</th>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2">Última execução</th>
                <th className="text-left px-3 py-2">Próxima</th>
                <th className="text-left px-3 py-2">Situação</th>
                {gerencia && <th className="text-right px-3 py-2">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <Linha
                  key={r.id}
                  r={r}
                  gerencia={gerencia}
                  selecionado={selecionados.has(r.id)}
                  onSelecionar={(v) => setSelecionados((s) => { const n = new Set(s); if (v) n.add(r.id); else n.delete(r.id); return n; })}
                  onAbrirOS={() => abrirOS.mutate([r])}
                  onMudou={atualizar}
                  onErro={falha("Não foi possível salvar.")}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Linha({
  r, gerencia, selecionado, onSelecionar, onAbrirOS, onMudou, onErro,
}: {
  r: MaintenancePlanRowDto;
  gerencia: boolean;
  selecionado: boolean;
  onSelecionar: (v: boolean) => void;
  onAbrirOS: () => void;
  onMudou: () => void;
  onErro: (e: unknown) => void;
}) {
  const [modo, setModo] = useState<null | "registrar" | "intervalo">(null);
  const s = r.status;

  const adotar = useMutation({
    mutationFn: () => api.post(`/maintenance/preventive/items/${r.id}/adopt-erp`),
    onSuccess: onMudou,
    onError: onErro,
  });

  const proxima = [
    s.nextKm != null ? `${fmtNumber(s.nextKm)} km` : null,
    s.nextDate ? fmtDay(s.nextDate) : null,
  ].filter(Boolean).join(" · ") || "—";

  return (
    <>
      <tr className={s.status === "vencido" ? "bg-danger-50/30" : ""}>
        {gerencia && (
          <td className="px-3 py-3 align-top">
            <input type="checkbox" checked={selecionado} disabled={!!r.openWorkOrder} onChange={(e) => onSelecionar(e.target.checked)} aria-label="Selecionar" />
          </td>
        )}
        <td className="px-3 py-3 align-top">
          <Link className="font-mono text-gray-900 hover:underline" to={`/frota/placa/${r.plate}`}>{r.plate}</Link>
          <p className="text-xs text-gray-400">{r.isTrailer ? "carreta" : "cavalo"}{r.vehicleModel ? ` · ${r.vehicleModel}` : ""}</p>
          {r.group && (
            <Link className="text-xs text-tm-600 hover:underline" to={`/frota/manutencao/grupos/${r.group.id}`} title={r.group.source === "manual" ? "Incluída à mão no grupo" : "Pelo modelo do ERP"}>
              {r.group.name}
            </Link>
          )}
        </td>
        <td className="px-3 py-3 align-top">
          <p className="text-gray-900">{r.label}</p>
          <p className="text-xs text-gray-500">
            {intervalText(r)} · <span title="De onde vem o intervalo">{PLAN_SOURCE_LABEL[r.source]}</span>
          </p>
        </td>
        <td className="px-3 py-3 align-top">
          {r.lastExecution ? (
            <>
              <p className="text-gray-900">{fmtDay(r.lastExecution.executedAt)}{r.lastExecution.odometerKm != null ? ` · ${fmtNumber(r.lastExecution.odometerKm)} km` : ""}</p>
              <p className="text-xs text-gray-400">{EXECUTION_SOURCE_LABEL[r.lastExecution.source]}</p>
            </>
          ) : (
            <p className="text-gray-400">—</p>
          )}
          {!r.lastExecution && r.erpReference && (
            <p className="text-xs text-gray-500 mt-1">
              ERP: {fmtDay(r.erpReference.date)} · {fmtNumber(r.erpReference.km)} km
              {gerencia && (
                <button className="ml-2 text-tm-600 hover:underline disabled:opacity-50" disabled={adotar.isPending} onClick={() => adotar.mutate()}>
                  adotar
                </button>
              )}
            </p>
          )}
        </td>
        <td className="px-3 py-3 align-top">
          <p className="text-gray-900">{proxima}</p>
          {r.currentKm && !r.isTrailer && (
            <p className={`text-xs ${s.kmStale ? "text-warning-700" : "text-gray-400"}`} title={r.currentKm.sourceLabel}>
              atual {fmtNumber(r.currentKm.km)} km{s.kmStale ? " (leitura antiga)" : ""}
            </p>
          )}
        </td>
        <td className="px-3 py-3 align-top">
          <PlanStatusBadge status={s.status} />
          <p className="text-xs text-gray-500 mt-1 max-w-[220px]">{s.reason}</p>
        </td>
        {gerencia && (
          <td className="px-3 py-3 align-top text-right whitespace-nowrap">
            {r.openWorkOrder ? (
              <Link className="badge-blue hover:underline" to={`/frota/manutencao/ordens/${r.openWorkOrder.id}`}>O.S. #{r.openWorkOrder.number}</Link>
            ) : (
              <button className="btn-ghost text-xs" onClick={onAbrirOS}>Abrir O.S.</button>
            )}
            <button className="btn-ghost text-xs" onClick={() => setModo(modo === "registrar" ? null : "registrar")}>Registrar</button>
            <button className="btn-ghost text-xs" onClick={() => setModo(modo === "intervalo" ? null : "intervalo")}>Intervalo</button>
          </td>
        )}
      </tr>
      {modo && (
        <tr className="bg-surface-muted">
          <td colSpan={gerencia ? 7 : 5} className="px-3 py-3">
            {modo === "registrar"
              ? <RegistrarExecucao r={r} onFeito={() => { setModo(null); onMudou(); }} onErro={onErro} />
              : <EditarIntervalo r={r} onFeito={() => { setModo(null); onMudou(); }} onErro={onErro} />}
          </td>
        </tr>
      )}
    </>
  );
}

function RegistrarExecucao({ r, onFeito, onErro }: { r: MaintenancePlanRowDto; onFeito: () => void; onErro: (e: unknown) => void }) {
  const [data, setData] = useState(() => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" }));
  const [km, setKm] = useState(r.currentKm && !r.isTrailer ? String(r.currentKm.km) : "");
  const [obs, setObs] = useState("");
  const exigeKm = !!r.intervalKm;
  const kmN = useMemo(() => parseBRNumber(km), [km]);

  const salvar = useMutation({
    mutationFn: () =>
      api.post("/maintenance/preventive/executions", {
        plate: r.plate,
        componentCode: r.componentCode,
        executedAt: new Date(`${data}T12:00:00-03:00`).toISOString(),
        odometerKm: kmN != null ? Math.round(kmN) : null,
        notes: obs.trim() || null,
      }),
    onSuccess: onFeito,
    onError: onErro,
  });

  return (
    <div className="flex flex-wrap items-end gap-2">
      <span className="text-sm text-gray-700 mr-2">Registrar <strong>{r.label}</strong> em {r.plate}:</span>
      <label className="block">
        <span className="text-xs text-gray-500">Data</span>
        <input type="date" className="input mt-0.5" value={data} onChange={(e) => setData(e.target.value)} />
      </label>
      <label className="block">
        <span className="text-xs text-gray-500">KM{exigeKm ? " *" : ""}</span>
        <input className="input mt-0.5 w-36" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} />
      </label>
      <label className="block flex-1 min-w-[200px]">
        <span className="text-xs text-gray-500">Observação</span>
        <input className="input mt-0.5 w-full" value={obs} maxLength={1000} placeholder="Ex.: feita na concessionária, NF 1234" onChange={(e) => setObs(e.target.value)} />
      </label>
      <button className="btn-primary" disabled={salvar.isPending || !data || (exigeKm && kmN == null)} onClick={() => salvar.mutate()}>
        Registrar
      </button>
    </div>
  );
}

function EditarIntervalo({ r, onFeito, onErro }: { r: MaintenancePlanRowDto; onFeito: () => void; onErro: (e: unknown) => void }) {
  const [km, setKm] = useState(r.intervalKm ? String(r.intervalKm) : "");
  const [dias, setDias] = useState(r.intervalDays ? String(r.intervalDays) : "");
  const kmN = parseBRNumber(km);
  const diasN = parseBRNumber(dias);

  const salvar = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/maintenance/preventive/items/${r.id}`, body),
    onSuccess: onFeito,
    onError: onErro,
  });

  return (
    <div className="flex flex-wrap items-end gap-2">
      <span className="text-sm text-gray-700 mr-2">Intervalo de <strong>{r.label}</strong> em {r.plate}:</span>
      <label className="block">
        <span className="text-xs text-gray-500">KM</span>
        <input className="input mt-0.5 w-36" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} />
      </label>
      <label className="block">
        <span className="text-xs text-gray-500">Dias</span>
        <input className="input mt-0.5 w-24" inputMode="numeric" value={dias} onChange={(e) => setDias(e.target.value)} />
      </label>
      <button
        className="btn-primary"
        disabled={salvar.isPending || (kmN == null && diasN == null)}
        onClick={() => salvar.mutate({ intervalKm: kmN != null ? Math.round(kmN) : null, intervalDays: diasN != null ? Math.round(diasN) : null })}
      >
        Salvar
      </button>
      <button className="btn-ghost text-danger-600" disabled={salvar.isPending} onClick={() => salvar.mutate({ active: false })}>
        Tirar do plano
      </button>
      <p className="w-full text-xs text-gray-500">
        Ajustado aqui, o intervalo deixa de acompanhar o ERP{r.source === "erp" ? " (hoje vem de lá)" : ""}.
      </p>
    </div>
  );
}
